import { useQueries } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import type { CallerIdentity } from "../../api/gateway";
import { quote, rupees, type CartMode } from "../../api/cart";
import { defaultDuration, durationLabel, familyOf, planFamilies, termDays } from "../../api/planFamily";
import type { RechargePlan } from "../../api/subscriberRecharge";

/** One duration's price as the server quoted it alone: amount and saving, by groupname. */
export type TermPrices = Record<string, { amount: number | null; saving: number }>;

/**
 * The server's quote of each duration of the chosen basic plan, plan alone,
 * so the duration step can show what a longer term saves. Kept out of
 * PlanPicker so the picker renders without a query client.
 */
export function usePlanTermPrices(identity: CallerIdentity, ns: string, mode: CartMode, accountid: string | undefined,
  plans: RechargePlan[], value: string, why: (p: RechargePlan) => string): TermPrices {
  const terms = familyOf(planFamilies(plans), value)?.plans ?? [];
  const results = useQueries({
    queries: terms.length > 1
      ? terms.map((pl) => ({
          queryKey: ["planterm", ns, mode, accountid ?? "", pl.groupname],
          enabled: Boolean(identity.token && ns && why(pl) === "" && (mode !== "recharge" || accountid)),
          queryFn: ({ signal }: { signal: AbortSignal }) =>
            quote(identity, ns, { plan: pl.groupname, addons: [], mode, ...(accountid ? { accountid } : {}) }, signal),
          retry: false,
          staleTime: 60_000,
        }))
      : [],
  });
  const out: TermPrices = {};
  terms.forEach((pl, i) => {
    const line = results[i]?.data?.lines[0];
    if (line) out[pl.groupname] = { amount: line.amount, saving: line.saving ?? 0 };
  });
  return out;
}

export interface PlanPickerProps {
  plans: RechargePlan[];
  /** Each duration's server price, from usePlanTermPrices; {} shows durations without prices. */
  termPrices: TermPrices;
  /** The chosen plan's groupname, "" for none. */
  value: string;
  onChange: (groupname: string) => void;
  /** Why a plan cannot be sold here, "" when it can. */
  why: (p: RechargePlan) => string;
  /** Extra words on a plan, e.g. "uses 1 pin". */
  tag?: (p: RechargePlan) => string;
  disabled?: boolean;
  helperText?: string;
  error?: boolean;
}

/**
 * Step 1, the basic plan; step 2, its duration (owner D2 2026-10-26: 30 days
 * first, the longer terms shown with what they save). Each duration's price
 * and saving is the server's quote for that plan alone -- the browser
 * computes nothing.
 */
export function PlanPicker(p: PlanPickerProps) {
  const families = planFamilies(p.plans);
  const family = familyOf(families, p.value);
  const terms = family?.plans ?? [];

  const pickFamily = (key: string) => {
    const f = families.find((x) => x.key === key);
    const d = f && defaultDuration(f, p.why);
    p.onChange(d?.groupname ?? "");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <TextField
        select
        fullWidth
        required
        size="small"
        label="Plan"
        value={family?.key ?? ""}
        onChange={(e) => pickFamily(e.target.value)}
        disabled={p.disabled || !p.plans.length}
        error={p.error}
        helperText={p.helperText}
      >
        {families.map((f) => {
          const sellable = f.plans.some((pl) => p.why(pl) === "");
          return (
            <MenuItem key={f.key} value={f.key} disabled={!sellable}>
              <Box>
                {f.label}
                <Typography variant="caption" display="block" color="text.secondary">
                  {sellable ? f.plans.map(durationLabel).join(" · ") : f.plans[0] ? p.why(f.plans[0]) : ""}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </TextField>
      {family && (
        <Box>
          <Typography variant="subtitle2">Duration</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }} role="radiogroup" aria-label="Duration">
            {terms.map((pl) => {
              const why = p.why(pl);
              const line = p.termPrices[pl.groupname];
              const words = [
                durationLabel(pl),
                line?.amount != null ? rupees(line.amount) : "",
                line?.saving ? `saves ${rupees(line.saving)}` : "",
                p.tag?.(pl) ?? "",
              ].filter(Boolean);
              return (
                <Chip
                  key={pl.groupname}
                  role="radio"
                  aria-checked={pl.groupname === p.value}
                  label={why ? `${durationLabel(pl)} (${why})` : words.join(" · ")}
                  color={pl.groupname === p.value ? "primary" : line?.saving ? "success" : "default"}
                  variant={pl.groupname === p.value ? "filled" : "outlined"}
                  disabled={p.disabled || Boolean(why)}
                  onClick={() => p.onChange(pl.groupname)}
                  data-term={termDays(pl)}
                />
              );
            })}
          </Box>
          {terms.length > 1 && (
            <Typography variant="caption" color="text.secondary">
              A longer term costs less than its months at the 30-day price; activation and some add-ons may be included.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
